import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { apiGet } from '../api/client.js';

/**
 * /roadmap — the workflow / "what's next" view.
 *
 * Owner/admin only. Three sections:
 *   1. Rollup — counts across the org (projects, pending posts, etc.)
 *   2. Operator actions — things only you can do (Meta App setup, B2 backups…)
 *   3. ADRs — every architecture decision with status + implementation progress
 */
const STATUS_KIND = {
  'Accepted':   'ok',
  'Proposed':   'queued',
  'Superseded': 'queued',
  'Rejected':   'fail',
  'Draft':      'queued',
  'Unknown':    'queued',
};

function fmtAgo(iso) {
  if (!iso) return '—';
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return iso; }
}

export default function Roadmap() {
  const q = useQuery({
    queryKey: ['roadmap'],
    queryFn: ({ signal }) => apiGet('/roadmap', { signal }),
  });

  if (q.isLoading) return <div className="loading">Loading roadmap…</div>;
  if (q.isError) return <div className="error">Couldn’t load roadmap: {q.error.message}</div>;

  const { adrs = [], operator_actions = [], rollup = {} } = q.data || {};
  const inProgressAdrs = adrs.filter((a) => a.total > 0 && a.done < a.total);
  const acceptedAdrs = adrs.filter((a) => a.status === 'Accepted' && (a.total === 0 || a.done >= a.total));
  const otherAdrs = adrs.filter((a) => a.status !== 'Accepted');

  return (
    <div className="roadmap-page">
      <header className="page-header">
        <h1>Roadmap</h1>
        <p className="muted">
          What's shipped, what's in flight, what's pending action. All architecture decisions, all operator
          to-dos, and the live state of the platform — one screen.
        </p>
      </header>

      <section className="account-section">
        <h2>At a glance</h2>
        <div className="org-stat-grid">
          <div className="org-stat">
            <span className="org-stat-val">{rollup.project_count || 0}</span>
            <span className="org-stat-lbl">active project{rollup.project_count === 1 ? '' : 's'}</span>
          </div>
          <div className="org-stat">
            <span className="org-stat-val">{rollup.drafts || 0}</span>
            <span className="org-stat-lbl">draft project{rollup.drafts === 1 ? '' : 's'}</span>
          </div>
          <div className="org-stat">
            <span className="org-stat-val">{rollup.posts_in_review || 0}</span>
            <span className="org-stat-lbl">post{rollup.posts_in_review === 1 ? '' : 's'} awaiting review</span>
          </div>
          <div className="org-stat">
            <span className="org-stat-val">{rollup.connected_accounts || 0}</span>
            <span className="org-stat-lbl">connected social account{rollup.connected_accounts === 1 ? '' : 's'}</span>
          </div>
          <div className="org-stat">
            <span className="org-stat-val">{fmtAgo(rollup.last_post_at)}</span>
            <span className="org-stat-lbl">last post generated</span>
          </div>
        </div>
      </section>

      <section className="account-section">
        <h2>Operator actions ({operator_actions.filter((a) => a.urgent).length} urgent)</h2>
        <p className="muted small">Things only you can do — external signups, secret pastes, App Reviews.</p>
        <div className="action-list">
          {operator_actions.map((a) => (
            <div key={a.id} className={`action-card ${a.urgent ? 'action-urgent' : ''}`}>
              <div className="action-head">
                {a.urgent && <span className="action-urgent-pill">URGENT</span>}
                <h3>{a.title}</h3>
              </div>
              <p>{a.detail}</p>
              {a.runbook && (
                <a href={`https://bitbucket.org/dclacorp/markaii/src/main/${a.runbook}`} target="_blank" rel="noreferrer" className="muted small">
                  📖 {a.runbook}
                </a>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="account-section">
        <h2>In flight ({inProgressAdrs.length})</h2>
        <p className="muted small">ADRs with implementation checklists still partially open.</p>
        {inProgressAdrs.length === 0 && <div className="empty">Nothing in progress. Pick from “Queued” below.</div>}
        {inProgressAdrs.map((adr) => <AdrRow key={adr.number} adr={adr} />)}
      </section>

      <section className="account-section">
        <h2>Shipped ({acceptedAdrs.length})</h2>
        <p className="muted small">Accepted ADRs with all implementation tasks closed.</p>
        {acceptedAdrs.map((adr) => <AdrRow key={adr.number} adr={adr} />)}
      </section>

      {otherAdrs.length > 0 && (
        <section className="account-section">
          <h2>Other ({otherAdrs.length})</h2>
          {otherAdrs.map((adr) => <AdrRow key={adr.number} adr={adr} />)}
        </section>
      )}
    </div>
  );
}

function AdrRow({ adr }) {
  const statusKind = STATUS_KIND[adr.status] || 'queued';
  const pct = adr.total ? Math.round((adr.done / adr.total) * 100) : null;
  return (
    <div className="adr-row">
      <span className="adr-number">ADR-{adr.number}</span>
      <span className="adr-title">{adr.title}</span>
      <span className={`adr-status adr-status-${statusKind}`}>{adr.status}</span>
      {pct !== null && (
        <span className="adr-progress">
          <span className="adr-progress-bar"><span style={{ width: `${pct}%` }} /></span>
          <span className="muted small">{adr.done}/{adr.total}</span>
        </span>
      )}
      {adr.context && <p className="adr-context">{adr.context}</p>}
    </div>
  );
}
